import React from 'react';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import RecetaModal from './RecetaModal';
import Form024Modal from './Form024Modal';
import ProteccionDatosModal from './ProteccionDatosModal';

/* Numeración FDI (WHO) — 32 piezas permanentes, cuadrantes 1-4 */
const CUADRANTES = [1, 2, 3, 4];
const numerosFDI = () => {
  const out = [];
  for (const cuad of CUADRANTES) for (let n = 8; n >= 1; n--) out.push(String(cuad * 10 + n));
  return out;
};

/* Posiciones exactas del arco — extraídas del diseño real de Figma
   (arch-canvas 334×190), no una aproximación. Cada número es el
   centro (x,y) de esa pieza dentro del lienzo original de Figma;
   se escalan para que el botón (más grande que el ícono compacto
   del diseño) tenga espacio cómodo para tocar. */
const ESCALA_ARCO = 1.86;
const RAW_POS_DIENTE = {
  18: [16, 31],       17: [34.67, 40.46], 16: [53.33, 48.56], 15: [72, 55.32],
  14: [90.67, 60.72], 13: [109.33, 64.78], 12: [128, 67.48],  11: [146.67, 68.83],
  21: [165.33, 68.83], 22: [184, 67.48],   23: [202.67, 64.78], 24: [221.33, 60.72],
  25: [240, 55.32],    26: [258.67, 48.56], 27: [277.33, 40.46], 28: [296, 31],
  48: [16, 127],       47: [34.67, 117.54], 46: [53.33, 109.44], 45: [72, 102.68],
  44: [90.67, 97.28],  43: [109.33, 93.22], 42: [128, 90.52],  41: [146.67, 89.17],
  31: [165.33, 89.17], 32: [184, 90.52],    33: [202.67, 93.22], 34: [221.33, 97.28],
  35: [240, 102.68],   36: [258.67, 109.44], 37: [277.33, 117.54], 38: [296, 127],
};
const NUMEROS_SUP = ['18', '17', '16', '15', '14', '13', '12', '11', '21', '22', '23', '24', '25', '26', '27', '28'];
const NUMEROS_INF = ['48', '47', '46', '45', '44', '43', '42', '41', '31', '32', '33', '34', '35', '36', '37', '38'];

const centroDiente = numero => {
  const [x, y] = RAW_POS_DIENTE[numero] || [0, 0];
  return { x: (x + 11) * ESCALA_ARCO, y: (y + 11) * ESCALA_ARCO };
};

const minYSup = Math.min(...NUMEROS_SUP.map(n => centroDiente(n).y));
const minYInf = Math.min(...NUMEROS_INF.map(n => centroDiente(n).y));
const maxYSup = Math.max(...NUMEROS_SUP.map(n => centroDiente(n).y));
const maxYInf = Math.max(...NUMEROS_INF.map(n => centroDiente(n).y));
const maxX    = Math.max(...Object.values(RAW_POS_DIENTE).map(([x]) => (x + 11) * ESCALA_ARCO));

const ANCHO_ARCO = maxX + 30;
const ALTO_ARCO  = Math.max(maxYSup - minYSup, maxYInf - minYInf) + 50;

const posArcoSup = numero => { const c = centroDiente(numero); return { x: c.x, y: c.y - minYSup + 25 }; };
const posArcoInf = numero => { const c = centroDiente(numero); return { x: c.x, y: c.y - minYInf + 25 }; };

const ESTADOS_PIEZA = [
  { cod: 'SAN', label: 'Sana' },
  { cod: 'AUS', label: 'Ausente' },
  { cod: 'EXT', label: 'Extracción indicada' },
  { cod: 'END', label: 'Endodoncia' },
  { cod: 'COR', label: 'Corona' },
  { cod: 'IMP', label: 'Implante' },
  { cod: 'FRA', label: 'Fractura' },
];

const ESTADOS_SUPERFICIE = [
  { cod: 'SAN', label: 'Sana' },
  { cod: 'CAR', label: 'Caries activa' },
  { cod: 'OBT', label: 'Obturado' },
];

/* Ícono decorativo por estado (no es la simbología oficial MSP —
   esa sigue viviendo en SIMBOLO_PIEZA/SIMBOLO_SUPERFICIE tal cual). */
const ICONO_ESTADO_PIEZA = {
  SAN: 'ti-check', AUS: 'ti-circle-dashed', EXT: 'ti-scissors',
  END: 'ti-syringe', COR: 'ti-crown', IMP: 'ti-bolt', FRA: 'ti-alert-triangle',
};
const ICONO_ESTADO_SUPERFICIE = { SAN: 'ti-check', CAR: 'ti-alert-circle', OBT: 'ti-shield-check' };

/* Símbolo MSP (Formulario 033) derivado automático del estado elegido —
   el médico sigue eligiendo el mismo chip de siempre, el símbolo solo se
   usa para mostrarlo/imprimirlo, no es un campo nuevo que llenar. */
/* Simbología oficial del Formulario 033 (MSP Ecuador): X = extracción,
   ▲ = endodoncia, C = corona. Implante y fractura no son categorías
   MSP -- se mantiene una convención propia (IM / !) que no choca con
   ninguna del formulario oficial. */
const SIMBOLO_PIEZA = { AUS: 'A', EXT: 'X', END: '▲', COR: 'C', IMP: 'IM', FRA: '!' };
const SIMBOLO_SUPERFICIE = { CAR: '○', OBT: '/' };

/* rojo = pendiente/problema, azul = tratamiento completado — convención LatAm */
const colorEstadoPieza = cod => ({
  SAN: 'var(--text3)',
  AUS: 'var(--text3)',
  EXT: 'var(--red)',
  FRA: 'var(--red)',
  END: 'var(--blue)',
  COR: 'var(--blue)',
  IMP: 'var(--blue)',
}[cod] || 'var(--text3)');

const colorEstadoSuperficie = cod => ({
  SAN: 'var(--bg)',
  CAR: 'var(--red)',
  OBT: 'var(--blue)',
}[cod] || 'var(--bg)');

/* Icono de diente en SVG (corona + raíz), coloreado según estado whole-tooth */
/* Molares/premolares (última cifra FDI 4-8) se dibujan como círculo,
   incisivos/caninos (última cifra 1-3) como cuadrado -- igual que el
   Formulario 033 real del MSP. */
const esPiezaPosterior = numero => parseInt(numero[1], 10) >= 4;

const COLOR_CARA = { SAN: 'var(--surface)', CAR: 'var(--red)', OBT: 'var(--blue)' };

/* Diagrama tipo "reloj" con las 5 caras (V arriba, D derecha, L abajo,
   M izquierda, O al centro), igual al odontograma oficial del MSP.
   Si la pieza tiene un estado whole-tooth relevante (extracción,
   endodoncia, corona, implante, fractura) se muestra el símbolo grande
   en vez de las caras, porque en esos casos las caras dejan de aplicar. */
function PiezaCarasSVG({ numero, estado, ausente, simbolo, caras }) {
  const R = 16, cx = 19, cy = 19, r0 = 7.5;
  const posterior = esPiezaPosterior(numero);
  const colorSimbolo = colorEstadoPieza(estado);

  if (ausente || estado === 'AUS') {
    return (
      <svg viewBox="0 0 38 38" width="36" height="36">
        {posterior
          ? <circle cx={cx} cy={cy} r={R} fill="none" stroke="var(--text3)" strokeWidth="1.6" strokeDasharray="3 3" opacity="0.6" />
          : <rect x={cx - R * 0.92} y={cy - R * 0.92} width={R * 1.84} height={R * 1.84} rx="4"
              fill="none" stroke="var(--text3)" strokeWidth="1.6" strokeDasharray="3 3" opacity="0.6" />}
        <text x={cx} y={cy + 5} textAnchor="middle" fontSize="13" fontWeight="800" fill="var(--text3)">A</text>
      </svg>
    );
  }

  const overrideSimbolo = estado && estado !== 'SAN' ? (simbolo || SIMBOLO_PIEZA[estado]) : null;

  return (
    <svg viewBox="0 0 38 38" width="36" height="36">
      {posterior
        ? <circle cx={cx} cy={cy} r={R} fill="var(--surface)" stroke="var(--text3)" strokeWidth="1.6" />
        : <rect x={cx - R * 0.92} y={cy - R * 0.92} width={R * 1.84} height={R * 1.84} rx="4"
            fill="var(--surface)" stroke="var(--text3)" strokeWidth="1.6" />}

      {!overrideSimbolo && (
        <>
          {['V', 'D', 'L', 'M'].map((cara, i) => {
            const a1 = (-135 + i * 90) * Math.PI / 180;
            const a2 = (-45 + i * 90) * Math.PI / 180;
            const pt = (ang, rad) => [cx + rad * Math.cos(ang), cy + rad * Math.sin(ang)];
            const [x1, y1] = pt(a1, r0), [x2, y2] = pt(a2, r0);
            const [x3, y3] = pt(a2, R), [x4, y4] = pt(a1, R);
            const d = `M${x1},${y1} L${x4},${y4} A${R},${R} 0 0 1 ${x3},${y3} L${x2},${y2} A${r0},${r0} 0 0 0 ${x1},${y1} Z`;
            return <path key={cara} d={d} fill={COLOR_CARA[caras?.[cara] || 'SAN']} stroke="var(--surface)" strokeWidth="0.6" />;
          })}
          <circle cx={cx} cy={cy} r={r0} fill={COLOR_CARA[caras?.O || 'SAN']} stroke="var(--text3)" strokeWidth="0.9" />
          <line x1={cx - R * 0.72} y1={cy - R * 0.72} x2={cx + R * 0.72} y2={cy + R * 0.72} stroke="var(--text3)" strokeWidth="0.7" opacity="0.75" />
          <line x1={cx + R * 0.72} y1={cy - R * 0.72} x2={cx - R * 0.72} y2={cy + R * 0.72} stroke="var(--text3)" strokeWidth="0.7" opacity="0.75" />
        </>
      )}

      {overrideSimbolo && (
        <text x={cx} y={cy + 5} textAnchor="middle" fontSize="16" fontWeight="800" fill={colorSimbolo}>{overrideSimbolo}</text>
      )}
    </svg>
  );
}

export default function Odontograma({ codPacienteProp, codHistorialClinico, embebido } = {}) {
  const [sp] = useSearchParams();
  const { usuario } = useAuth();
  // Si viene embebido dentro de "Nueva consulta" (Historia.jsx), el
  // paciente ya está resuelto y no hace falta el buscador propio.
  const [codPac, setCodPac]   = useState(codPacienteProp || sp.get('paciente') || '');
  const [pacBusq, setPacBusq] = useState('');
  const [pacSugs, setPacSugs] = useState([]);
  const [paciente, setPac]    = useState(null);
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [piezaSel, setPiezaSel] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [loadDet, setLoadDet] = useState(false);
  const [superficieSel, setSuperficieSel] = useState(null);
  const [tab, setTab] = useState('overview'); // 'overview' | 'historial' | 'imagenes'
  const [historial, setHistorial] = useState([]);
  const [msg, setMsg] = useState('');
  const [recetaModalAbierto, setRecetaModalAbierto] = useState(false);
  const [form024Abierto, setForm024Abierto] = useState(false);
  const [proteccionDatosAbierto, setProteccionDatosAbierto] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formPieza, setFormPieza] = useState({ C_ESTADO_PIEZA: 'SAN', TRATAMIENTO: '', OBSERVACIONES: '' });
  const [ultimoTratamientoCod, setUltimoTratamientoCod] = useState(null);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [tratamientoParaFoto, setTratamientoParaFoto] = useState('');
  const [formSup, setFormSup] = useState({ C_ESTADO_SUPERFICIE: 'SAN', TRATAMIENTO: '', OBSERVACIONES: '' });

  const [plan, setPlan] = useState(null);
  const [catalogo, setCatalogo] = useState([]);
  const [showAgregarPrestacion, setShowAgregarPrestacion] = useState(false);
  const [descuentoLineaAbierto, setDescuentoLineaAbierto] = useState(false);
  const [nuevaLinea, setNuevaLinea] = useState({ NUMERO_PIEZA: '', C_SUPERFICIE: '', NOMBRE_TRATAMIENTO: '', PRECIO: '', DESCUENTO: 0 });
  const [descuentoEdit, setDescuentoEdit] = useState('');
  const esAdmin = usuario?.esDBA || (usuario?.tipo_usuario || '').toUpperCase().trim() === 'ADMINISTRADOR';

  const [antecedentes, setAntecedentes] = useState(null);
  const [editandoAntecedentes, setEditandoAntecedentes] = useState(false);
  const [antecedentesForm, setAntecedentesForm] = useState({});
  const [alergias, setAlergias] = useState([]);
  const [nuevaAlergia, setNuevaAlergia] = useState('');
  const [signosVitales, setSignosVitales] = useState(null);
  const [editandoVitales, setEditandoVitales] = useState(false);
  const [vitalesForm, setVitalesForm] = useState({ PRESION_ARTERIAL: '', FRECUENCIA_CARDIACA: '', TEMPERATURA: '' });

  useEffect(() => {
    if (!pacBusq.trim()) { setPacSugs([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/pacientes?buscar=${encodeURIComponent(pacBusq)}`);
        setPacSugs(data.slice ? data.slice(0, 8) : []);
      } catch (e) { setPacSugs([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [pacBusq]);

  useEffect(() => {
    if (!codPac) return;
    cargarPaciente();
    cargarOverview();
    cargarAntecedentes();
    cargarAlergias();
    cargarSignosVitales();
    cargarConvenio();
  }, [codPac]);

  const cargarAntecedentes = async () => {
    try {
      const { data } = await api.get(`/odontograma/antecedentes/${codPac}`);
      setAntecedentes(data);
      setAntecedentesForm(data || {});
    } catch (e) { setAntecedentes(null); }
  };

  const guardarAntecedentes = async () => {
    try {
      await api.put(`/odontograma/antecedentes/${codPac}`, antecedentesForm);
      setEditandoAntecedentes(false);
      await cargarAntecedentes();
    } catch (e) {
      setMsg('✕ No se pudieron guardar los antecedentes');
    }
  };

  const cargarAlergias = async () => {
    try {
      const { data } = await api.get(`/odontograma/alergias/${codPac}/activas`);
      setAlergias(data || []);
    } catch (e) { setAlergias([]); }
  };

  const agregarAlergia = async () => {
    if (!nuevaAlergia.trim()) return;
    try {
      await api.post(`/odontograma/alergias/${codPac}`, { ALERGIA: nuevaAlergia.trim() });
      setNuevaAlergia('');
      await cargarAlergias();
    } catch (e) {
      setMsg('✕ No se pudo registrar la alergia');
    }
  };

  const quitarAlergia = async alergiaTexto => {
    try {
      await api.delete(`/odontograma/alergias/${codPac}`, { data: { ALERGIA: alergiaTexto } });
      await cargarAlergias();
    } catch (e) {
      setMsg('✕ No se pudo quitar la alergia');
    }
  };

  const cargarSignosVitales = async () => {
    try {
      const { data } = await api.get(`/odontograma/signos-vitales/${codPac}/ultimo`);
      setSignosVitales(data);
    } catch (e) { setSignosVitales(null); }
  };

  const guardarSignosVitales = async () => {
    try {
      await api.post(`/odontograma/signos-vitales/${codPac}`, vitalesForm);
      setEditandoVitales(false);
      setVitalesForm({ PRESION_ARTERIAL: '', FRECUENCIA_CARDIACA: '', TEMPERATURA: '' });
      await cargarSignosVitales();
    } catch (e) {
      setMsg('✕ No se pudieron guardar los signos vitales');
    }
  };

  const [convenio, setConvenio] = useState('');

  const cargarPaciente = async () => {
    try {
      const { data } = await api.get(`/pacientes/${codPac}`);
      setPac(data);
    } catch (e) { /* silencioso */ }
  };

  const cargarConvenio = async () => {
    try {
      const { data } = await api.get(`/historia/convenio/${codPac}`);
      setConvenio(data?.CONVENIO || 'PARTICULAR');
    } catch (e) { setConvenio('PARTICULAR'); }
  };

  const calcularEdad = fechaNacimiento => {
    if (!fechaNacimiento) return null;
    const hoy = new Date(); const nac = new Date(fechaNacimiento);
    let y = hoy.getFullYear() - nac.getFullYear();
    if (hoy.getMonth() < nac.getMonth() || (hoy.getMonth() === nac.getMonth() && hoy.getDate() < nac.getDate())) y--;
    return y;
  };

  const cargarOverview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/odontograma/${codPac}`);
      setOverview(data);
    } catch (e) {
      setMsg('✕ No se pudo cargar el odontograma');
    } finally {
      setLoading(false);
    }
  };

  const cargarPlan = async () => {
    try {
      const { data } = await api.get(`/odontograma/plan/${codPac}`);
      setPlan(data);
      setDescuentoEdit(String(data.descuentoComercial));
    } catch (e) {
      setPlan(null);
      const detalle = e.response?.data?.error || e.message || 'sin detalle';
      setMsg('✕ No se pudo cargar el presupuesto (' + detalle + '). Verifica que el backend de odontograma esté actualizado.');
    }
  };

  const cargarCatalogo = async () => {
    try {
      const { data } = await api.get('/odontograma/catalogo/lista');
      setCatalogo(data);
    } catch (e) { setCatalogo([]); }
  };

  useEffect(() => {
    if (codPac) { cargarPlan(); cargarCatalogo(); }
  }, [codPac]);

  const guardarDescuento = async () => {
    if (!plan) { setMsg('✕ El presupuesto todavía no ha cargado — espera un momento o recarga la página'); return; }
    const val = Number(descuentoEdit);
    if (isNaN(val) || val < 0 || val > 100) { setMsg('✕ El descuento debe ser un número entre 0 y 100'); return; }
    try {
      await api.put(`/odontograma/plan/${plan.COD}/descuento`, { DESCUENTO_COMERCIAL: val });
      await cargarPlan();
    } catch (e) {
      setMsg('✕ No se pudo actualizar el descuento: ' + (e.response?.data?.error || e.message || 'sin detalle'));
    }
  };

  const agregarLinea = async () => {
    if (!plan) { setMsg('✕ El presupuesto todavía no ha cargado — espera un momento o recarga la página'); return; }
    if (!nuevaLinea.NUMERO_PIEZA.trim()) { setMsg('✕ Escribe el número de pieza'); return; }
    if (!nuevaLinea.NOMBRE_TRATAMIENTO.trim()) { setMsg('✕ Escribe qué se va a hacer'); return; }
    if (nuevaLinea.PRECIO === '' || isNaN(Number(nuevaLinea.PRECIO)) || Number(nuevaLinea.PRECIO) < 0) {
      setMsg('✕ Escribe un precio válido'); return;
    }
    try {
      const nombreEscrito = nuevaLinea.NOMBRE_TRATAMIENTO.trim();
      let codTratamiento = catalogo.find(c => c.NOMBRE.toLowerCase() === nombreEscrito.toLowerCase())?.COD;

      // Si escribió una prestación nueva que no está en el catálogo, se crea al vuelo
      if (!codTratamiento) {
        const { data } = await api.post('/odontograma/catalogo', {
          NOMBRE: nombreEscrito,
          PRECIO_BASE: Number(nuevaLinea.PRECIO),
        });
        codTratamiento = data.COD;
        await cargarCatalogo();
      }

      await api.post(`/odontograma/plan/${plan.COD}/lineas`, {
        NUMERO_PIEZA: nuevaLinea.NUMERO_PIEZA,
        C_SUPERFICIE: nuevaLinea.C_SUPERFICIE || null,
        COD_TRATAMIENTO: codTratamiento,
        PRECIO: Number(nuevaLinea.PRECIO),
        DESCUENTO: Number(nuevaLinea.DESCUENTO) || 0,
      });
      setMsg('✓ Prestación agregada al plan');
      setNuevaLinea({ NUMERO_PIEZA: '', C_SUPERFICIE: '', NOMBRE_TRATAMIENTO: '', PRECIO: '', DESCUENTO: 0 });
      setDescuentoLineaAbierto(false);
      setShowAgregarPrestacion(false);
      await cargarPlan();
    } catch (e) {
      const detalle = e.response?.data?.error || e.message || 'sin detalle del servidor';
      setMsg('✕ No se pudo agregar la prestación: ' + detalle);
    }
  };

  const quitarLinea = async codLinea => {
    try {
      await api.delete(`/odontograma/plan/lineas/${codLinea}`);
      await cargarPlan();
    } catch (e) {
      setMsg('✕ No se pudo quitar la prestación: ' + (e.response?.data?.error || e.message || 'sin detalle'));
    }
  };

  const imprimirOdontograma = () => {
    if (!codPac) return;
    api.get(`/odontograma/${codPac}/pdf`, { responseType: 'blob' })
      .then(({ data }) => {
        const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
        window.open(url, '_blank');
      })
      .catch(() => setMsg('✕ No se pudo generar el PDF del odontograma'));
  };

  const abrirPieza = async numeroPieza => {
    setPiezaSel(numeroPieza);
    setSuperficieSel(null);
    setLoadDet(true);
    try {
      const { data } = await api.get(`/odontograma/pieza/${codPac}/${numeroPieza}`);
      setDetalle(data);
      setFormPieza({ C_ESTADO_PIEZA: data.estado || 'SAN', TRATAMIENTO: '', OBSERVACIONES: '' });
    } catch (e) {
      setMsg('✕ No se pudo cargar la pieza');
    } finally {
      setLoadDet(false);
    }
  };

  const guardarPieza = async () => {
    if (!formPieza.TRATAMIENTO.trim()) { setMsg('✕ Describe el tratamiento realizado'); return; }
    setSaving(true); setMsg('');
    try {
      const { data } = await api.post('/odontograma/tratamiento', {
        COD_PACIENTE: codPac, NUMERO_PIEZA: piezaSel,
        C_ESTADO_PIEZA: formPieza.C_ESTADO_PIEZA,
        SIMBOLO: SIMBOLO_PIEZA[formPieza.C_ESTADO_PIEZA] || null,
        TRATAMIENTO: formPieza.TRATAMIENTO,
        OBSERVACIONES: formPieza.OBSERVACIONES || null,
        COD_HISTORIAL_CLINICO: codHistorialClinico || null,
      });
      setMsg('✓ Pieza actualizada');
      setUltimoTratamientoCod(data.codTratamiento);
      await cargarOverview();
      await abrirPieza(piezaSel);
    } catch (e) {
      setMsg('✕ ' + (e.response?.data?.error || 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const abrirSuperficie = superficie => {
    setSuperficieSel(superficie);
    const actual = detalle?.superficies?.find(s => s.superficie === superficie);
    setFormSup({ C_ESTADO_SUPERFICIE: actual?.estado || 'SAN', TRATAMIENTO: '', OBSERVACIONES: '' });
  };

  const guardarSuperficie = async () => {
    if (!formSup.TRATAMIENTO.trim()) { setMsg('✕ Describe el tratamiento realizado'); return; }
    setSaving(true); setMsg('');
    try {
      const { data } = await api.post('/odontograma/superficie', {
        COD_PACIENTE: codPac, NUMERO_PIEZA: piezaSel, C_SUPERFICIE: superficieSel,
        C_ESTADO_SUPERFICIE: formSup.C_ESTADO_SUPERFICIE,
        SIMBOLO: SIMBOLO_SUPERFICIE[formSup.C_ESTADO_SUPERFICIE] || null,
        TRATAMIENTO: formSup.TRATAMIENTO,
        OBSERVACIONES: formSup.OBSERVACIONES || null,
        COD_HISTORIAL_CLINICO: codHistorialClinico || null,
      });
      setMsg('✓ Superficie actualizada');
      setUltimoTratamientoCod(data.codTratamiento);
      await cargarOverview();
      await abrirPieza(piezaSel);
      setSuperficieSel(null);
    } catch (e) {
      setMsg('✕ ' + (e.response?.data?.error || 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const subirFoto = async (file, tipoArchivo = 'FOTO', codTratamientoDestino = null) => {
    const destino = codTratamientoDestino || ultimoTratamientoCod;
    if (!destino || !file) return;
    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('tipoArchivo', tipoArchivo);
    setSubiendoFoto(true);
    try {
      await api.post(`/odontograma/tratamiento/${destino}/archivo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMsg('✓ Foto/radiografía adjuntada');
      if (tab === 'historial' || tab === 'imagenes') await cargarHistorial();
    } catch (e) {
      setMsg('✕ ' + (e.response?.data?.error || 'Error al subir el archivo'));
    } finally {
      setSubiendoFoto(false);
    }
  };

  const eliminarFoto = async codArchivo => {
    try {
      await api.delete(`/odontograma/archivo/${codArchivo}`);
      await cargarHistorial();
    } catch (e) {
      setMsg('✕ ' + (e.response?.data?.error || 'Error al eliminar'));
    }
  };

  const cargarHistorial = async () => {
    try {
      const { data } = await api.get(`/odontograma/tratamientos/${codPac}`);
      setHistorial(data);
    } catch (e) { setHistorial([]); }
  };

  useEffect(() => {
    if ((tab === 'historial' || tab === 'imagenes') && codPac) cargarHistorial();
  }, [tab, codPac]);

  const hallazgoDe = numero => overview.find(o => o.numeroPieza === numero);

  /* ═══ SIN PACIENTE ═══ */
  if (!codPac) {
    return (
      <>
        <style>{css}</style>
        <div className="od-search-wrap">
          <h2 className="od-search-title">Odontograma — buscar paciente</h2>
          <input className="od-search-input" placeholder="Buscar por nombre o cédula..."
            value={pacBusq} onChange={e => setPacBusq(e.target.value)} autoFocus />
          {pacSugs.length > 0 && (
            <div className="od-search-sugs">
              {pacSugs.map(p => (
                <div key={p.COD} className="od-search-sug"
                  onClick={() => { setCodPac(String(p.COD)); setPacBusq(''); setPacSugs([]); }}>
                  <div className="od-search-sug-nom">{[p.APELLIDO_PATERNO, p.APELLIDO_MATERNO, p.PRIMER_NOMBRE].filter(Boolean).join(' ')}</div>
                  <div className="od-search-sug-ci">CI: {p.IDENTIFICACION || '—'}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    );
  }

  /* ═══ CON PACIENTE ═══ */
  return (
    <>
      <style>{css}</style>
      {msg && <div className={`od-msg ${msg.startsWith('✓') ? 'ok' : 'err'}`}>{msg}</div>}
      {ultimoTratamientoCod && (
        <div className="od-foto-upload">
          <label className="od-foto-btn">
            {subiendoFoto ? 'Subiendo...' : (<><i className="ti ti-camera" aria-hidden="true"></i> Adjuntar foto/radiografía a lo último guardado</>)}
            <input type="file" accept="image/*" hidden disabled={subiendoFoto}
              onChange={e => { if (e.target.files[0]) subirFoto(e.target.files[0]); e.target.value = ''; }} />
          </label>
        </div>
      )}

      <div className="od-page">
      <div className="od-boceto">
        <div className="patient-bar">
          <div className="boceto-avatar-row">
            <div className="boceto-avatar">
              {[paciente?.PRIMER_NOMBRE?.[0], paciente?.APELLIDO_PATERNO?.[0]].filter(Boolean).join('')}
            </div>
            <div className="patient-id">
              <div className="patient-name">{[paciente?.APELLIDO_PATERNO, paciente?.APELLIDO_MATERNO, paciente?.PRIMER_NOMBRE].filter(Boolean).join(' ')}</div>
            </div>
          </div>
          <div className="patient-meta">
            <div className="meta-field">
              <span className="label">CI</span>
              <span className="value mono">{paciente?.IDENTIFICACION || '—'}</span>
            </div>
            <div className="meta-field">
              <span className="label">Edad</span>
              <span className="value">{calcularEdad(paciente?.FECHA_NACIMIENTO) ?? '—'}</span>
            </div>
            <div className="meta-field">
              <span className="label">Sexo</span>
              <span className="value">{paciente?.C_SEXO || '—'}</span>
            </div>
            <div className="meta-field">
              <span className="label">Teléfono</span>
              <span className="value">{paciente?.TELEFONO || '—'}</span>
            </div>
            <div className="meta-field">
              <span className="label">Convenio</span>
              <span className="value">{convenio || '—'}</span>
            </div>
            <div className="meta-field">
              <span className="label">Alergias</span>
              <span className={`boceto-badge ${alergias.length > 0 ? 'boceto-badge-danger' : ''}`}>
                {alergias.length > 0 ? `${alergias.length} registrada${alergias.length > 1 ? 's' : ''}` : 'Sin alergias'}
              </span>
            </div>
          </div>
          <div className="bar-actions">
            <button className="btn btn-ghost" onClick={imprimirOdontograma}>
              <i className="ti ti-file-type-pdf" aria-hidden="true"></i> PDF
            </button>
            <button className="btn btn-accent" onClick={() => { setCodPac(''); setPac(null); setOverview([]); setDetalle(null); setPiezaSel(null); }}>
              <i className="ti ti-user-search" aria-hidden="true"></i> Cambiar paciente
            </button>
          </div>
        </div>

        <div className="status-rail">
          <div className="vital-card">
            <span className="label"><i className="ti ti-heart-rate-monitor" aria-hidden="true"></i>Presión</span>
            {editandoVitales ? (
              <input className="boceto-input" placeholder="120/80"
                value={vitalesForm.PRESION_ARTERIAL} onChange={e => setVitalesForm(f => ({ ...f, PRESION_ARTERIAL: e.target.value }))} />
            ) : (
              <span className="value" onClick={() => setEditandoVitales(true)}>{signosVitales?.PRESION_ARTERIAL || '—'}</span>
            )}
          </div>
          <div className="vital-card">
            <span className="label"><i className="ti ti-heartbeat" aria-hidden="true"></i>Frec. cardíaca</span>
            {editandoVitales ? (
              <input className="boceto-input" type="number" placeholder="76"
                value={vitalesForm.FRECUENCIA_CARDIACA} onChange={e => setVitalesForm(f => ({ ...f, FRECUENCIA_CARDIACA: e.target.value }))} />
            ) : (
              <span className="value" onClick={() => setEditandoVitales(true)}>{signosVitales?.FRECUENCIA_CARDIACA || '—'}<small>lpm</small></span>
            )}
          </div>
          <div className="vital-card">
            <span className="label"><i className="ti ti-temperature" aria-hidden="true"></i>Temperatura</span>
            {editandoVitales ? (
              <div className="boceto-input-row">
                <input className="boceto-input" type="number" step="0.1" placeholder="36.5"
                  value={vitalesForm.TEMPERATURA} onChange={e => setVitalesForm(f => ({ ...f, TEMPERATURA: e.target.value }))} />
                <button className="btn btn-accent boceto-mini" onClick={guardarSignosVitales}><i className="ti ti-check" aria-hidden="true"></i></button>
                <button className="btn btn-ghost boceto-mini" onClick={() => setEditandoVitales(false)}><i className="ti ti-x" aria-hidden="true"></i></button>
              </div>
            ) : (
              <span className="value" onClick={() => setEditandoVitales(true)}>{signosVitales?.TEMPERATURA || '—'}<small>°C</small></span>
            )}
          </div>

          <div className="allergy-alert">
            <span className="label"><i className="ti ti-alert-triangle" aria-hidden="true"></i>Alergias</span>
            <div className="tag-row">
              {alergias.map(a => (
                <span key={a.COD} className="tag tag-danger">
                  <i className="ti ti-pill" aria-hidden="true"></i>{a.ALERGIA}
                  <button className="boceto-tag-x" onClick={() => quitarAlergia(a.ALERGIA)} title="Quitar"><i className="ti ti-x" aria-hidden="true"></i></button>
                </span>
              ))}
              <input className="boceto-tag-input" placeholder="+ agregar..."
                value={nuevaAlergia} onChange={e => setNuevaAlergia(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') agregarAlergia(); }} />
            </div>
          </div>
        </div>

        <div className="antecedentes-card">
          <span className="label">Antecedentes</span>
          {editandoAntecedentes ? (
            <div className="boceto-antecedentes-edit">
              {[
                ['ALERGIAS', 'Alergias'], ['DIABETES', 'Diabetes'], ['HEMORRAGICOS', 'Hemorrágicos'],
                ['VIH_SIDA', 'VIH/SIDA'], ['TUBERCULOSIS', 'Tuberculosis'], ['ASMA', 'Asma'],
                ['HIPERTENSION', 'Hipertensión'], ['ENF_CARDIACA', 'Enf. cardíaca'],
              ].map(([campo, label]) => (
                <label key={campo} className="boceto-chk">
                  <input type="checkbox" checked={!!antecedentesForm[campo]}
                    onChange={e => setAntecedentesForm(f => ({ ...f, [campo]: e.target.checked }))} />
                  {label}
                </label>
              ))}
              <input className="boceto-input" placeholder="Otro..." style={{ marginTop: 6, width: '100%' }}
                value={antecedentesForm.OTRO || ''} onChange={e => setAntecedentesForm(f => ({ ...f, OTRO: e.target.value }))} />
              <div className="boceto-input-row" style={{ marginTop: 6 }}>
                <button className="btn btn-accent boceto-mini" onClick={guardarAntecedentes}><i className="ti ti-check" aria-hidden="true"></i> Guardar</button>
                <button className="btn btn-ghost boceto-mini" onClick={() => setEditandoAntecedentes(false)}><i className="ti ti-x" aria-hidden="true"></i></button>
              </div>
            </div>
          ) : (
            <div className="tag-row" onClick={() => setEditandoAntecedentes(true)} style={{ cursor: 'pointer' }}>
              {[
                ['ALERGIAS', 'Alergias', 'ti-alert-triangle'], ['DIABETES', 'Diabetes', 'ti-droplet'],
                ['HEMORRAGICOS', 'Hemorrágicos', 'ti-droplet-half-2'], ['VIH_SIDA', 'VIH/SIDA', 'ti-virus'],
                ['TUBERCULOSIS', 'Tuberculosis', 'ti-lungs'], ['ASMA', 'Asma', 'ti-lungs'],
                ['HIPERTENSION', 'Hipertensión', 'ti-heart'], ['ENF_CARDIACA', 'Enf. cardíaca', 'ti-heartbeat'],
              ].filter(([campo]) => antecedentes?.[campo]).map(([campo, label, icono]) => (
                <span key={campo} className="tag"><i className={`ti ${icono}`} aria-hidden="true"></i>{label}</span>
              ))}
              {antecedentes?.OTRO && <span className="tag">{antecedentes.OTRO}</span>}
              {!antecedentes && <span className="tag" style={{ borderStyle: 'dashed' }}>Sin registrar — clic para agregar</span>}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <div className="od-tabs">
          <div className={`od-tab ${tab === 'overview' ? 'act' : ''}`} onClick={() => setTab('overview')}>
            <i className="ti ti-dental" aria-hidden="true"></i> Odontograma
          </div>
          <div className={`od-tab ${tab === 'imagenes' ? 'act' : ''}`} onClick={() => setTab('imagenes')}>
            <i className="ti ti-camera" aria-hidden="true"></i> Imágenes
          </div>
          <div className={`od-tab ${tab === 'historial' ? 'act' : ''}`} onClick={() => setTab('historial')}>
            <i className="ti ti-history" aria-hidden="true"></i> Historial
          </div>
        </div>

        {tab === 'overview' && (
          <div className="od-body od-body-conpresupuesto">
            <div className="od-presupuesto-col">
              <div className="od-presupuesto">
                <div className="od-presupuesto-hdr">Presupuesto total</div>
                <div className="od-presupuesto-total">${plan?.total?.toFixed(2) ?? '0.00'}</div>

                <div className="od-presupuesto-desc">
                  <span>Descuento comercial</span>
                  {esAdmin ? (
                    <div className="od-presupuesto-desc-edit">
                      <input type="number" min="0" max="100" value={descuentoEdit}
                        onChange={e => setDescuentoEdit(e.target.value)} />
                      <span>%</span>
                      <button className="btn-mo-sec" style={{ padding: '3px 8px', fontSize: 11 }} onClick={guardarDescuento} title="Guardar descuento">
                        <i className="ti ti-check" aria-hidden="true"></i>
                      </button>
                    </div>
                  ) : (
                    <span>{plan?.descuentoComercial ?? 0}%</span>
                  )}
                </div>

                {plan?.lineas?.length > 0 && (
                  <div className="od-presupuesto-lineas">
                    {plan.lineas.map(l => (
                      <div key={l.COD} className="od-presupuesto-linea">
                        <div className="od-presupuesto-linea-txt">
                          <div className="od-presupuesto-linea-tit">
                            Pieza {l.NUMERO_PIEZA}{l.C_SUPERFICIE ? ` (${l.C_SUPERFICIE})` : ''} — {l.TRATAMIENTO_NOMBRE}
                          </div>
                          <div className="od-presupuesto-linea-sub">
                            ${Number(l.PRECIO).toFixed(2)}{l.DESCUENTO > 0 ? ` · -${l.DESCUENTO}%` : ''}
                            {l.C_ESTADO_PAGO === 'PAG' ? ' · Pagado' : l.C_ESTADO_PAGO === 'PAR' ? ' · Pago parcial' : ''}
                          </div>
                        </div>
                        {l.C_ESTADO_PAGO !== 'PAG' && (
                          <button className="od-presupuesto-linea-del" onClick={() => quitarLinea(l.COD)} title="Quitar">
                            <i className="ti ti-x" aria-hidden="true"></i>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <button className="btn-mo-sec" style={{ width: '100%', marginTop: 12 }}
                  onClick={() => {
                    if (!showAgregarPrestacion && piezaSel) {
                      setNuevaLinea(f => ({ ...f, NUMERO_PIEZA: piezaSel }));
                    }
                    setShowAgregarPrestacion(v => !v);
                  }}>
                  <i className="ti ti-plus" aria-hidden="true"></i> Agregar prestación
                </button>

                {showAgregarPrestacion && (
                  <div className="od-presupuesto-form">
                    <p className="od-form-hint">
                      Agrega un cargo al presupuesto de este paciente: elige la pieza, escribe qué se va a hacer y el precio.
                    </p>

                    <div className="od-form-row">
                      <div>
                        <div className="mo-sec">Pieza *</div>
                        <input className="form-control" placeholder="Ej: 18"
                          value={nuevaLinea.NUMERO_PIEZA}
                          onChange={e => setNuevaLinea(f => ({ ...f, NUMERO_PIEZA: e.target.value }))} />
                      </div>
                      <div>
                        <div className="mo-sec">Superficie</div>
                        <select className="form-control" value={nuevaLinea.C_SUPERFICIE}
                          onChange={e => setNuevaLinea(f => ({ ...f, C_SUPERFICIE: e.target.value }))}>
                          <option value="">Pieza completa</option>
                          {['M', 'D', 'V', 'L', 'O'].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    {piezaSel && (
                      <p className="od-form-tip">Pieza {piezaSel} tomada automáticamente de la que tienes abierta abajo.</p>
                    )}

                    <div className="mo-sec" style={{ marginTop: 10 }}>¿Qué se va a hacer? *</div>
                    <input className="form-control" list="od-catalogo-list" placeholder="Ej: Obturación resina compuesta"
                      value={nuevaLinea.NOMBRE_TRATAMIENTO}
                      onChange={e => {
                        const nombre = e.target.value;
                        const t = catalogo.find(c => c.NOMBRE.toLowerCase() === nombre.toLowerCase());
                        setNuevaLinea(f => ({ ...f, NOMBRE_TRATAMIENTO: nombre, PRECIO: t ? String(t.PRECIO_BASE) : f.PRECIO }));
                      }} />
                    <datalist id="od-catalogo-list">
                      {catalogo.map(c => <option key={c.COD} value={c.NOMBRE} />)}
                    </datalist>
                    {catalogo.length === 0 ? (
                      <p className="od-form-tip">Aún no tienes ninguna prestación guardada — escribe el nombre y el precio, y quedará guardada para la próxima vez.</p>
                    ) : (
                      <p className="od-form-tip">Empieza a escribir para elegir una ya guardada, o escribe una nueva.</p>
                    )}

                    <div className="mo-sec" style={{ marginTop: 10 }}>Precio *</div>
                    <input className="form-control" type="number" step="0.01" placeholder="0.00"
                      value={nuevaLinea.PRECIO}
                      onChange={e => setNuevaLinea(f => ({ ...f, PRECIO: e.target.value }))} />

                    {nuevaLinea.DESCUENTO > 0 || descuentoLineaAbierto ? (
                      <>
                        <div className="mo-sec" style={{ marginTop: 10 }}>Descuento en esta prestación (%)</div>
                        <input className="form-control" type="number" min="0" max="100"
                          value={nuevaLinea.DESCUENTO}
                          onChange={e => setNuevaLinea(f => ({ ...f, DESCUENTO: e.target.value }))} />
                      </>
                    ) : (
                      <button className="od-form-link" onClick={() => setDescuentoLineaAbierto(true)}>
                        <i className="ti ti-plus" aria-hidden="true"></i> Agregar descuento a esta prestación
                      </button>
                    )}

                    <button className="btn-mo-pri" style={{ width: '100%', marginTop: 12 }} onClick={agregarLinea}>
                      <i className="ti ti-check" aria-hidden="true"></i> Agregar al plan
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="od-odontograma-col">
            <div className="od-legend">
              <span><i className="od-dot" style={{ background: 'var(--red)' }}></i> Caries / patología actual</span>
              <span><i className="od-dot" style={{ background: 'var(--blue)' }}></i> Obturado / tratamiento realizado</span>
              <span><i className="od-dot" style={{ border: '1px dashed var(--border)', background: 'transparent' }}></i> Ausente</span>
              <span style={{ color: 'var(--red)', fontWeight: 800 }}>X</span><span>Extracción indicada</span>
              <span style={{ color: 'var(--blue)', fontWeight: 800 }}>▲</span><span>Endodoncia</span>
              <span style={{ color: 'var(--blue)', fontWeight: 800 }}>C</span><span>Corona</span>
            </div>

            {loading ? <div className="spin-wrap"><div className="sp"/></div> : (
              <div className="od-arcada-wrap">
                <div className="od-arco od-arco-sup" style={{ width: ANCHO_ARCO, height: ALTO_ARCO }}>
                  <div className="od-arco-divisor" />
                  {NUMEROS_SUP.map(numero => {
                      const h = hallazgoDe(numero);
                      const pos = posArcoSup(numero);
                      return (
                        <button key={numero}
                          className={`od-diente-btn ${piezaSel === numero ? 'sel' : ''}`}
                          style={{
                            position: 'absolute', left: pos.x, top: pos.y,
                            transform: 'translate(-50%, -50%)', alignItems: 'center',
                          }}
                          onClick={() => abrirPieza(numero)}>
                          <PiezaCarasSVG numero={numero} estado={h?.estado} ausente={h?.estado === 'AUS'} simbolo={h?.simbolo} caras={h?.caras} />
                          {h?.tieneHallazgo && <span className="od-diente-flag" title="Tiene hallazgos por superficie" />}
                        </button>
                      );
                    })}
                </div>

                <div className="od-arcada-nums">
                  {[1, 2].flatMap(cuad => numerosFDI().filter(n => n[0] === String(cuad))).map(numero => (
                    <span key={numero} className="od-num-cell">{numero}</span>
                  ))}
                </div>
                <div className="od-arcada-linea"><span className="od-arcada-linea-c" /></div>
                <div className="od-arcada-nums">
                  {[4, 3].flatMap(cuad => numerosFDI().filter(n => n[0] === String(cuad))).map(numero => (
                    <span key={numero} className="od-num-cell">{numero}</span>
                  ))}
                </div>

                <div className="od-arco od-arco-inf" style={{ width: ANCHO_ARCO, height: ALTO_ARCO }}>
                  <div className="od-arco-divisor" />
                  {NUMEROS_INF.map(numero => {
                      const h = hallazgoDe(numero);
                      const pos = posArcoInf(numero);
                      return (
                        <button key={numero}
                          className={`od-diente-btn ${piezaSel === numero ? 'sel' : ''}`}
                          style={{
                            position: 'absolute', left: pos.x, top: pos.y,
                            transform: 'translate(-50%, -50%)', alignItems: 'center',
                          }}
                          onClick={() => abrirPieza(numero)}>
                          <PiezaCarasSVG numero={numero} estado={h?.estado} ausente={h?.estado === 'AUS'} simbolo={h?.simbolo} caras={h?.caras} />
                          {h?.tieneHallazgo && <span className="od-diente-flag" title="Tiene hallazgos por superficie" />}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            {piezaSel && (
              <div className="od-detalle">
                <div className="od-detalle-hdr">
                  <span>Pieza {piezaSel}</span>
                  <button className="btn-mo-sec" style={{ padding: '4px 10px' }} onClick={() => { setPiezaSel(null); setDetalle(null); }} title="Cerrar">
                    <i className="ti ti-x" aria-hidden="true"></i>
                  </button>
                </div>

                {loadDet ? <div className="spin-wrap"><div className="sp"/></div> : detalle && (
                  <div className="od-detalle-body">
                    <div className="od-detalle-col">
                      <div className="mo-sec">Estado general de la pieza</div>
                      <div className="od-chip-row">
                        {ESTADOS_PIEZA.map(e => (
                          <button key={e.cod} type="button"
                            className={`od-chip ${formPieza.C_ESTADO_PIEZA === e.cod ? 'act' : ''}`}
                            onClick={() => setFormPieza(f => ({ ...f, C_ESTADO_PIEZA: e.cod }))}>
                            <i className={`ti ${ICONO_ESTADO_PIEZA[e.cod]}`} aria-hidden="true"></i> {e.label}
                          </button>
                        ))}
                      </div>
                      <div className="mo-sec" style={{ marginTop: 10 }}>Tratamiento realizado *</div>
                      <input className="form-control" placeholder="Ej: Extracción simple pieza 18"
                        value={formPieza.TRATAMIENTO} onChange={e => setFormPieza(f => ({ ...f, TRATAMIENTO: e.target.value }))} />
                      <div className="mo-sec" style={{ marginTop: 10 }}>Observaciones</div>
                      <textarea className="form-control" rows={2}
                        value={formPieza.OBSERVACIONES} onChange={e => setFormPieza(f => ({ ...f, OBSERVACIONES: e.target.value }))} />
                      <button className="btn-mo-pri" style={{ marginTop: 10 }} disabled={saving} onClick={guardarPieza}>
                        <i className="ti ti-device-floppy" aria-hidden="true"></i> {saving ? 'Guardando...' : 'Guardar estado de la pieza'}
                      </button>
                      <div className="od-acciones-pieza">
                        <button className="od-accion-item" onClick={() => setRecetaModalAbierto(true)}>
                          <span className="od-accion-ic od-accion-ic-blue"><i className="ti ti-prescription" aria-hidden="true"></i></span>
                          Generar receta
                        </button>
                        <button className="od-accion-item" onClick={() => setForm024Abierto(true)}>
                          <span className="od-accion-ic od-accion-ic-green"><i className="ti ti-file-check" aria-hidden="true"></i></span>
                          Consentimiento del procedimiento
                        </button>
                        <button className="od-accion-item" onClick={() => setProteccionDatosAbierto(true)}>
                          <span className="od-accion-ic od-accion-ic-amber"><i className="ti ti-shield-lock" aria-hidden="true"></i></span>
                          Protección de datos
                        </button>
                      </div>
                    </div>

                    <div className="od-detalle-col">
                      <div className="mo-sec">Superficies (clic para marcar hallazgo)</div>
                      <div className="od-sup-legend">
                        <span><b className="od-sup-legend-sym" style={{ color: 'var(--red)' }}>○</b> Caries</span>
                        <span><b className="od-sup-legend-sym" style={{ color: 'var(--blue)' }}>/</b> Restauración</span>
                      </div>
                      <div className="od-cruz">
                        {['V', 'M', 'O', 'D', 'L'].map(s => {
                          const estadoSup = detalle.superficies.find(x => x.superficie === s)?.estado;
                          const simboloSup = SIMBOLO_SUPERFICIE[estadoSup];
                          return (
                            <button key={s} className={`od-sup od-sup-${s.toLowerCase()}`}
                              style={{ background: colorEstadoSuperficie(estadoSup) }}
                              title={`Superficie ${s}`}
                              onClick={() => abrirSuperficie(s)}>
                              <span className="od-sup-letra">{s}</span>
                              {simboloSup && (
                                <span className="od-sup-simbolo"
                                  style={{ color: estadoSup === 'CAR' ? 'var(--red)' : 'var(--blue)' }}>
                                  {simboloSup}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {superficieSel && (
                        <div className="od-sup-form">
                          <div className="mo-sec">Superficie {superficieSel}</div>
                          <div className="od-chip-row">
                            {ESTADOS_SUPERFICIE.map(e => (
                              <button key={e.cod} type="button"
                                className={`od-chip ${formSup.C_ESTADO_SUPERFICIE === e.cod ? 'act' : ''}`}
                                onClick={() => setFormSup(f => ({ ...f, C_ESTADO_SUPERFICIE: e.cod }))}>
                                <i className={`ti ${ICONO_ESTADO_SUPERFICIE[e.cod]}`} aria-hidden="true"></i> {e.label}
                              </button>
                            ))}
                          </div>
                          <div className="mo-sec" style={{ marginTop: 10 }}>Tratamiento realizado *</div>
                          <input className="form-control" placeholder="Ej: Obturación resina compuesta"
                            value={formSup.TRATAMIENTO} onChange={e => setFormSup(f => ({ ...f, TRATAMIENTO: e.target.value }))} />
                          <div className="mo-sec" style={{ marginTop: 10 }}>Observaciones</div>
                          <textarea className="form-control" rows={2}
                            value={formSup.OBSERVACIONES} onChange={e => setFormSup(f => ({ ...f, OBSERVACIONES: e.target.value }))} />
                          <button className="btn-mo-pri" style={{ marginTop: 10 }} disabled={saving} onClick={guardarSuperficie}>
                            <i className="ti ti-device-floppy" aria-hidden="true"></i> {saving ? 'Guardando...' : 'Guardar superficie'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        )}

        {tab === 'imagenes' && (
          <div className="od-body">
            <div className="od-img-uploader">
              <select className="form-control" value={tratamientoParaFoto} onChange={e => setTratamientoParaFoto(e.target.value)}>
                <option value="">Asociar a...</option>
                {historial.map(h => (
                  <option key={h.COD} value={h.COD}>
                    Pieza {h.NUMERO_PIEZA}{h.C_SUPERFICIE ? ` · Sup. ${h.C_SUPERFICIE}` : ''} — {h.TRATAMIENTO} ({h.FECHA?.slice(0, 10)})
                  </option>
                ))}
              </select>
              <label className={`od-img-upload-btn ${!tratamientoParaFoto || subiendoFoto ? 'disabled' : ''}`}>
                <i className="ti ti-photo-plus" aria-hidden="true"></i> {subiendoFoto ? 'Subiendo...' : 'Cargar imagen'}
                <input type="file" accept="image/*" hidden disabled={!tratamientoParaFoto || subiendoFoto}
                  onChange={e => { if (e.target.files[0]) subirFoto(e.target.files[0], 'FOTO', tratamientoParaFoto); e.target.value = ''; }} />
              </label>
              {!tratamientoParaFoto && historial.length > 0 && (
                <span className="od-img-uploader-hint">Elige a qué tratamiento va la imagen antes de cargarla</span>
              )}
              {historial.length === 0 && (
                <span className="od-img-uploader-hint">Primero guarda un estado de pieza o superficie — la imagen siempre va ligada a un tratamiento registrado</span>
              )}
            </div>

            {(() => {
              const todasLasFotos = historial.flatMap(h =>
                (h.archivos || []).map(a => ({ ...a, _pieza: h.NUMERO_PIEZA, _superficie: h.C_SUPERFICIE, _fecha: h.FECHA, _tratamiento: h.TRATAMIENTO }))
              );
              if (todasLasFotos.length === 0) {
                return <div className="empty-hc">Todavía no hay fotos ni radiografías adjuntadas.</div>;
              }
              return (
                <div className="od-img-grid">
                  {todasLasFotos.map(a => (
                    <div key={a.COD} className="od-img-card">
                      <a href={`/${a.RUTA_ARCHIVO}`} target="_blank" rel="noreferrer" className="od-img-thumb">
                        <img src={`/${a.RUTA_ARCHIVO}`} alt={a.TIPO_ARCHIVO} />
                      </a>
                      <div className="od-img-body">
                        <div className="od-img-tit">Pieza {a._pieza}{a._superficie ? ` · Sup. ${a._superficie}` : ''}</div>
                        <div className="od-img-sub">{a._fecha?.slice(0, 10)}</div>
                      </div>
                      <button className="od-img-del" title="Eliminar" onClick={() => eliminarFoto(a.COD)}>
                        <i className="ti ti-trash" aria-hidden="true"></i>
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {tab === 'historial' && (
          <div className="od-body">
            {historial.length === 0 ? (
              <div className="empty-hc">Sin tratamientos registrados</div>
            ) : (
              <div className="od-hist-list">
                {historial.map(h => (
                  <div key={h.COD} className="od-hist-row">
                    <div className="od-hist-fecha">{h.FECHA?.slice(0,10)}</div>
                    <div className="od-hist-body">
                      <div className="od-hist-tratamiento">Pieza {h.NUMERO_PIEZA}{h.C_SUPERFICIE ? ` · Sup. ${h.C_SUPERFICIE}` : ''} — {h.TRATAMIENTO}</div>
                      <div className="od-hist-sub">{h.USUARIO_NOMBRE || h.USUARIO_REGISTRO} · Estado resultante: {h.C_ESTADO_RESULTANTE}</div>
                      {h.OBSERVACIONES && <div className="od-hist-obs">{h.OBSERVACIONES}</div>}
                      {h.archivos?.length > 0 && (
                        <div className="od-hist-fotos">
                          {h.archivos.map(a => (
                            <div key={a.COD} className="od-hist-foto-item">
                              <a href={`/${a.RUTA_ARCHIVO}`} target="_blank" rel="noreferrer">
                                <img src={`/${a.RUTA_ARCHIVO}`} alt={a.TIPO_ARCHIVO} />
                              </a>
                              <button className="od-hist-foto-del" title="Eliminar"
                                onClick={() => eliminarFoto(a.COD)}><i className="ti ti-x" aria-hidden="true"></i></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </div>

      {recetaModalAbierto && (
        <RecetaModal
          codPacienteInicial={codPac}
          descInicial={formPieza.TRATAMIENTO || `Pieza ${piezaSel}`}
          onGuardado={() => { setRecetaModalAbierto(false); setMsg('✓ Receta creada'); }}
          onCerrar={() => setRecetaModalAbierto(false)}
        />
      )}

      {form024Abierto && (
        <Form024Modal
          codPacienteInicial={codPac}
          numeroPiezaInicial={piezaSel}
          procedimientoInicial={formPieza.TRATAMIENTO || `Tratamiento pieza ${piezaSel}`}
          onGuardado={() => { setForm024Abierto(false); setMsg('✓ Consentimiento generado'); }}
          onCerrar={() => setForm024Abierto(false)}
        />
      )}

      {proteccionDatosAbierto && (
        <ProteccionDatosModal
          codPacienteInicial={codPac}
          onGuardado={() => { setProteccionDatosAbierto(false); setMsg('✓ Consentimiento generado'); }}
          onCerrar={() => setProteccionDatosAbierto(false)}
        />
      )}
    </>
  );
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');

.od-search-wrap { max-width:440px; margin:60px auto; text-align:center; }
.od-search-title { font-size:16px; font-weight:700; color:var(--text); margin-bottom:16px; }
.od-search-input { width:100%; padding:10px 14px; border:1px solid var(--border); border-radius:9px; font-size:14px; background:var(--surface); color:var(--text); outline:none; }
.od-search-input:focus { border-color:var(--navy-lt); }
.od-search-sugs { margin-top:8px; border:1px solid var(--border); border-radius:9px; overflow:hidden; text-align:left; background:var(--surface); }
.od-search-sug { padding:9px 14px; cursor:pointer; border-bottom:1px solid var(--bg); }
.od-search-sug:hover { background:var(--bg); }
.od-search-sug-nom { font-size:13px; font-weight:600; color:var(--text); }
.od-search-sug-ci { font-size:11px; color:var(--text3); }

.od-msg { padding:8px 14px; border-radius:8px; font-size:12px; margin-bottom:8px; }
.od-msg.ok  { background:var(--green-lt); color:var(--green); border:1px solid var(--green); }
.od-msg.err { background:var(--red-lt);   color:var(--red);   border:1px solid var(--red); }
.od-foto-upload { margin-bottom:10px; }
.od-foto-btn { display:inline-block; background:var(--blue-lt); color:var(--navy); border:1px dashed var(--navy-lt); border-radius:8px; padding:8px 14px; font-size:12px; cursor:pointer; }
.od-hist-fotos { display:flex; gap:6px; margin-top:8px; flex-wrap:wrap; }
.od-hist-foto-item { position:relative; width:52px; height:52px; }
.od-hist-foto-item img { width:100%; height:100%; object-fit:cover; border-radius:8px; border:1px solid var(--border); }
.od-hist-foto-del { position:absolute; top:-5px; right:-5px; width:16px; height:16px; border-radius:50%; background:var(--red); color:#fff; border:none; font-size:9px; cursor:pointer; }

.od-page { display:flex; flex-direction:column; gap:12px; }

/* ═══════════ Bloque calcado literal del boceto (fuentes/colores propios,
   no los tokens del resto de la app) — franja de paciente/vitales/antecedentes/alergias ═══════════ */
.od-boceto {
  --bg:#eef1f5; --surface:#ffffff; --surface-alt:#f2f4f8; --border:#dfe4eb; --border-strong:#c7cfda;
  --ink:#1b2333; --ink-soft:#5f6b7d; --ink-faint:#94a0b0;
  --accent:#c81f68; --accent-ink:#ffffff;
  --danger:#bd2a2a; --danger-soft:#fbe8e6; --danger-border:#f0bcb5;
  --shadow: 0 1px 2px rgba(27,35,51,.04), 0 8px 24px -12px rgba(27,35,51,.12);
  font-family:'IBM Plex Sans', system-ui, sans-serif;
  display:flex; flex-direction:column; gap:16px;
}
.od-boceto .patient-bar{background:var(--surface);border:1px solid var(--border);border-radius:14px;box-shadow:var(--shadow);padding:18px 22px;display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:16px;}
.od-boceto .patient-id{display:flex;flex-direction:column;gap:6px;min-width:220px;}
.od-boceto .boceto-avatar-row{display:flex;align-items:center;gap:12px;}
.od-boceto .boceto-avatar{width:42px;height:42px;border-radius:50%;background:var(--accent);color:#fff;display:flex;align-items:center;justify-content:center;font-family:'Fraunces',serif;font-weight:600;font-size:15px;flex-shrink:0;}
.od-boceto .boceto-badge{font-size:11.5px;font-weight:600;color:var(--ink-soft);}
.od-boceto .boceto-badge-danger{color:var(--danger);}
.od-boceto .patient-name{font-family:'Fraunces', serif;font-weight:600;font-size:23px;color:var(--ink);}
.od-boceto .patient-meta{display:flex;flex-wrap:wrap;gap:22px;row-gap:8px;}
.od-boceto .meta-field{display:flex;flex-direction:column;gap:3px;min-width:104px;}
.od-boceto .meta-field .label{font-size:11px;color:var(--ink-faint);}
.od-boceto .meta-field .value{font-size:14px;font-weight:500;color:var(--ink);}
.od-boceto .meta-field .value.mono{font-family:'IBM Plex Mono',monospace;}
.od-boceto .bar-actions{display:flex;gap:10px;align-items:center;}
.od-boceto .btn{font-family:inherit;font-size:13.5px;font-weight:600;border-radius:9px;padding:10px 16px;border:1px solid transparent;cursor:pointer;display:inline-flex;align-items:center;gap:7px;}
.od-boceto .btn-ghost{background:var(--surface);border-color:var(--border-strong);color:var(--ink-soft);}
.od-boceto .btn-accent{background:var(--accent);color:var(--accent-ink);}
.od-boceto .status-rail{display:grid;grid-template-columns:repeat(3, minmax(140px,1fr)) minmax(220px,1.4fr);gap:12px;}
@media (max-width:860px){ .od-boceto .status-rail{grid-template-columns:repeat(2,1fr);} }
.od-boceto .vital-card{background:var(--surface);border:1px solid var(--border);border-radius:12px;padding:12px 14px;display:flex;flex-direction:column;gap:2px;}
.od-boceto .vital-card .label{font-size:11px;color:var(--ink-faint);}
.od-boceto .vital-card .label i,.od-boceto .tag i,.od-boceto .allergy-alert .label i{font-size:13px;vertical-align:-2px;margin-right:5px;}
.od-boceto .vital-card .value{font-family:'IBM Plex Mono',monospace;font-size:19px;font-weight:500;cursor:pointer;color:var(--ink);}
.od-boceto .vital-card .value small{font-size:11px;color:var(--ink-soft);font-family:'IBM Plex Sans',sans-serif;font-weight:400;margin-left:3px;}
.od-boceto .antecedentes-card{background:var(--surface);border:1px solid var(--border);border-radius:12px;padding:12px 14px;display:flex;flex-direction:column;gap:8px;justify-content:center;}
.od-boceto .antecedentes-card .label{font-size:11px;color:var(--ink-faint);}
.od-boceto .tag-row{display:flex;flex-wrap:wrap;gap:6px;}
.od-boceto .tag{font-size:12.5px;font-weight:500;padding:4px 10px;border-radius:999px;border:1px solid var(--border-strong);background:var(--surface-alt);color:var(--ink-soft);display:inline-flex;align-items:center;}
.od-boceto .allergy-alert{background:var(--danger-soft);border:1px solid var(--danger-border);border-radius:12px;padding:12px 14px;display:flex;flex-direction:column;gap:8px;justify-content:center;}
.od-boceto .allergy-alert .label{font-size:11.5px;font-weight:600;color:var(--danger);display:flex;align-items:center;gap:6px;text-transform:uppercase;letter-spacing:.03em;}
.od-boceto .tag-danger{background:var(--surface);border-color:var(--danger-border);color:var(--danger);font-weight:600;}
.od-boceto .boceto-tag-x{background:none;border:none;color:inherit;cursor:pointer;opacity:.6;padding:0 0 0 5px;display:flex;}
.od-boceto .boceto-tag-x:hover{opacity:1;}
.od-boceto .boceto-tag-input{border:1px dashed var(--border-strong);background:none;border-radius:999px;padding:4px 10px;font-size:12.5px;width:90px;color:var(--ink);}
.od-boceto .boceto-tag-input:focus{outline:none;border-color:var(--accent);}
.od-boceto .boceto-input{width:100%;padding:5px 8px;font-size:13px;border:1px solid var(--border-strong);border-radius:6px;background:var(--surface);color:var(--ink);font-family:'IBM Plex Mono',monospace;}
.od-boceto .boceto-input-row{display:flex;gap:6px;align-items:center;}
.od-boceto .boceto-mini{padding:5px 8px;font-size:11px;}
.od-boceto .boceto-antecedentes-edit{display:flex;flex-direction:column;gap:4px;}
.od-boceto .boceto-chk{display:flex;align-items:center;gap:6px;font-size:12px;color:var(--ink-soft);cursor:pointer;}
.od-hdr  { display:flex; align-items:center; justify-content:space-between; background:var(--navy); color:#fff; padding:14px 18px; border-radius:12px; }
.od-hdr-name { font-size:15px; font-weight:700; }
.od-hdr-sub  { font-size:12px; opacity:.8; margin-top:2px; }

.od-vitales-rail { display:grid; grid-template-columns:1fr 1.5fr 1fr; background:var(--surface); border:1px solid var(--border); border-radius:12px; }
@media (max-width:860px) { .od-vitales-rail { grid-template-columns:1fr; } }
.od-vitales-col { padding:12px 16px; }
.od-vitales-col-mid { border-left:1px solid var(--border); border-right:1px solid var(--border); }
@media (max-width:860px) { .od-vitales-col-mid { border-left:none; border-right:none; border-top:1px solid var(--border); border-bottom:1px solid var(--border); } }
.od-vitales-lbl { font-size:11px; color:var(--text3); display:flex; align-items:center; gap:5px; margin-bottom:8px; }
.od-vitales-valores { display:flex; gap:18px; cursor:pointer; }
.od-vitales-valores span { display:flex; flex-direction:column; align-items:flex-start; gap:1px; }
.od-vitales-valores i { font-size:17px; color:var(--text3); margin-bottom:2px; }
.od-vitales-valores b { font-size:15px; font-weight:700; color:var(--text); }
.od-vitales-valores small { font-size:9.5px; color:var(--text3); }
.od-vitales-vacio, .od-vitales-vacio-txt { background:none; border:none; font-size:11.5px; color:var(--navy-lt); cursor:pointer; padding:0; }
.od-vitales-edit { display:flex; flex-direction:column; gap:6px; max-width:220px; }
.od-vitales-edit input { padding:6px 10px; font-size:11.5px; border-radius:8px; }
.od-vitales-edit-btns { display:flex; gap:6px; }
.od-mini-btn { display:inline-flex; align-items:center; gap:4px; border:1px solid var(--border); background:var(--surface); border-radius:8px; padding:5px 10px; font-size:11px; font-weight:600; color:var(--text2); cursor:pointer; }
.od-mini-btn:hover { background:var(--bg); }
.od-mini-btn-pri { background:var(--navy); border-color:var(--navy); color:#fff; }
.od-mini-btn-pri:hover { background:var(--navy); opacity:.9; }
.od-antecedentes-edit { display:flex; flex-direction:column; gap:4px; max-width:260px; }
.od-antecedente-chk { display:flex; align-items:center; gap:6px; font-size:11.5px; color:var(--text2); cursor:pointer; }
.od-tag-row { display:flex; flex-wrap:wrap; gap:6px; cursor:pointer; min-height:26px; align-items:center; }
.od-tag { display:inline-flex; align-items:center; gap:5px; font-size:11.5px; font-weight:600; padding:5px 12px; border-radius:20px; }
.od-tag-amber { background:var(--amber-lt, #fdf1de); color:var(--amber); }
.od-tag-danger { background:var(--red-lt); color:var(--red); }
.od-tag-danger button { background:none; border:none; color:inherit; cursor:pointer; opacity:.6; padding:0; display:flex; margin-left:2px; }
.od-tag-danger button:hover { opacity:1; }
.od-alergias-lbl { color:var(--red); }
.od-alergia-input { border:1px dashed var(--border); background:none; border-radius:20px; padding:5px 12px; font-size:11.5px; width:100px; }
.od-alergia-input:focus { outline:none; border-color:var(--navy-lt); }

.od-tabs { display:flex; gap:4px; border-bottom:1px solid var(--border); }
.od-tab  { padding:9px 16px; font-size:13px; cursor:pointer; color:var(--text3); border-bottom:2px solid transparent; display:flex; align-items:center; gap:6px; }
.od-tab.act { color:var(--navy); border-bottom-color:var(--navy-lt); font-weight:600; }

.od-body { display:flex; flex-direction:column; gap:14px; }
.od-body-conpresupuesto { display:grid; grid-template-columns:260px 1fr; align-items:start; gap:16px; }
@media (max-width:860px) { .od-body-conpresupuesto { grid-template-columns:1fr; } }
.od-odontograma-col { display:flex; flex-direction:column; gap:14px; min-width:0; }

.od-presupuesto { background:var(--surface); border:1px solid var(--border); border-radius:12px; padding:16px; }
.od-presupuesto-hdr { font-size:11px; color:var(--text3); text-transform:uppercase; letter-spacing:.3px; }
.od-presupuesto-total { font-size:26px; font-weight:700; color:var(--navy); margin:2px 0 12px; }
.od-presupuesto-desc { display:flex; align-items:center; justify-content:space-between; font-size:12px; color:var(--text2); padding-top:10px; border-top:1px solid var(--border); }
.od-presupuesto-desc-edit { display:flex; align-items:center; gap:4px; }
.od-presupuesto-desc-edit input { width:44px; padding:3px 5px; font-size:11px; border:1px solid var(--border); border-radius:6px; background:var(--bg); color:var(--text); }
.od-presupuesto-lineas { display:flex; flex-direction:column; gap:8px; margin-top:12px; padding-top:12px; border-top:1px solid var(--border); }
.od-presupuesto-linea { display:flex; align-items:flex-start; justify-content:space-between; gap:6px; }
.od-presupuesto-linea-tit { font-size:11.5px; font-weight:600; color:var(--text); line-height:1.3; }
.od-presupuesto-linea-sub { font-size:10.5px; color:var(--text3); margin-top:1px; }
.od-presupuesto-linea-del { background:none; border:none; color:var(--text3); cursor:pointer; padding:2px; flex-shrink:0; }
.od-presupuesto-linea-del:hover { color:var(--red); }
.od-presupuesto-form { margin-top:12px; padding-top:12px; border-top:1px solid var(--border); }
.od-form-hint { font-size:11.5px; color:var(--text2); line-height:1.4; margin:0 0 10px; }
.od-form-tip { font-size:10.5px; color:var(--text3); margin:4px 0 0; line-height:1.4; }
.od-form-row { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
.od-form-link { background:none; border:none; color:var(--navy-lt); font-size:11.5px; cursor:pointer; padding:6px 0; text-align:left; }
.od-form-link:hover { text-decoration:underline; }
.od-legend { display:flex; gap:16px; flex-wrap:wrap; font-size:11px; color:var(--text2); }
.od-dot { display:inline-block; width:10px; height:10px; border-radius:50%; margin-right:5px; vertical-align:-1px; }

.od-arcada-wrap { background:var(--surface); border:1px solid var(--border); border-radius:12px; padding:22px 18px; overflow-x:auto; }
.od-arco { position:relative; margin:0 auto; }
.od-arco-divisor { position:absolute; top:0; bottom:0; left:50%; width:1px; background:var(--border); z-index:0; }
.od-arcada { display:flex; justify-content:center; gap:0; }
.od-cuadrante { display:flex; gap:0; padding:0 5px; }
.od-cuad-der { border-right:1.5px solid var(--border); }
.od-diente-btn { position:relative; width:42px; display:flex; align-items:flex-end; justify-content:center; background:none; border:1px solid transparent; border-radius:6px; padding:4px 0; cursor:pointer; }
.od-arcada-sup .od-diente-btn { align-items:flex-end; }
.od-arcada-inf .od-diente-btn { align-items:flex-start; }
.od-diente-btn:hover { background:var(--bg); }
.od-diente-btn.sel { border-color:var(--navy-lt); background:var(--blue-lt); }
.od-diente-flag { position:absolute; top:2px; right:1px; width:6px; height:6px; border-radius:50%; background:var(--amber); }
.od-arcada-nums { display:flex; justify-content:center; }
.od-num-cell { flex-shrink:0; width:42px; text-align:center; font-size:11px; font-weight:700; color:var(--text2); padding:2px 0; }
.od-arcada-linea { display:flex; align-items:center; padding:1px 0; }
.od-arcada-linea-c { display:block; width:100%; height:1px; background:var(--border); }

.od-detalle { border:1px solid var(--border); border-radius:12px; overflow:hidden; }
.od-detalle-hdr { padding:9px 14px; background:var(--surface2); border-bottom:1px solid var(--border); display:flex; align-items:center; justify-content:space-between; font-size:13px; font-weight:700; color:var(--text); }
.od-detalle-body { display:grid; grid-template-columns:1fr 1fr; gap:16px; padding:16px; }
@media (max-width:720px) { .od-detalle-body { grid-template-columns:1fr; } }
.od-detalle-col { display:flex; flex-direction:column; }

.od-chip-row { display:flex; flex-wrap:wrap; gap:6px; }
.od-chip { padding:5px 11px; border-radius:20px; border:1px solid var(--border); background:var(--bg); font-size:11px; cursor:pointer; color:var(--text2); }
.od-chip.act { background:var(--navy); border-color:var(--navy); color:#fff; }

.od-sup-legend { display:flex; gap:14px; font-size:10.5px; color:var(--text2); margin:2px 0 4px; }
.od-sup-legend-sym { font-size:13px; font-weight:800; margin-right:4px; }
.od-cruz { position:relative; width:180px; height:180px; margin:10px auto; }
.od-sup { position:absolute; border:1px solid var(--border); cursor:pointer; color:var(--text); display:flex; flex-direction:column; align-items:center; justify-content:center; gap:2px; transition:filter .1s; }
.od-sup:hover { filter:brightness(0.97); }
.od-sup-letra { font-size:9px; font-weight:700; opacity:.5; letter-spacing:.3px; }
.od-sup-simbolo { font-size:17px; font-weight:800; line-height:1; }
.od-sup-v { top:0; left:50px; width:80px; height:50px; border-radius:8px 8px 0 0; }
.od-sup-m { left:0; top:50px; width:50px; height:80px; border-radius:8px 0 0 8px; }
.od-sup-d { right:0; top:50px; width:50px; height:80px; border-radius:0 8px 8px 0; }
.od-sup-l { bottom:0; left:50px; width:80px; height:50px; border-radius:0 0 8px 8px; }
.od-sup-o { left:50px; top:50px; width:80px; height:80px; border-radius:50%; }
.od-sup-form { margin-top:12px; padding-top:12px; border-top:1px solid var(--border); }

.od-hist-list { display:flex; flex-direction:column; gap:8px; }
.od-hist-row { display:flex; gap:12px; padding:10px 14px; border:1px solid var(--border); border-radius:9px; background:var(--surface); }
.od-hist-fecha { font-size:11px; color:var(--text3); flex-shrink:0; width:70px; }
.od-hist-tratamiento { font-size:13px; font-weight:600; color:var(--text); }
.od-hist-sub { font-size:11px; color:var(--text3); margin-top:2px; }
.od-hist-obs { font-size:11px; color:var(--text2); margin-top:4px; font-style:italic; }

.od-acciones-pieza { display:flex; flex-direction:column; gap:8px; margin-top:10px; }
.od-accion-item { display:flex; align-items:center; gap:10px; text-align:left; padding:9px 12px; border:1px solid var(--border); border-radius:10px; background:var(--surface); font-size:12.5px; font-weight:600; color:var(--text); cursor:pointer; transition:background .12s, border-color .12s; }
.od-accion-item:hover, .od-accion-item:focus-visible { background:var(--bg); border-color:var(--navy-lt); outline:none; }
.od-accion-ic { width:26px; height:26px; border-radius:50%; display:flex; align-items:center; justify-content:center; flex-shrink:0; font-size:14px; }
.od-accion-ic-blue { background:var(--blue-lt); color:var(--navy); }
.od-accion-ic-green { background:var(--green-lt); color:var(--green); }
.od-accion-ic-amber { background:var(--amber-lt, #fdf1de); color:var(--amber); }

.od-img-uploader { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:16px; }
.od-img-uploader select { max-width:280px; margin:0; }
.od-img-upload-btn { display:inline-flex; align-items:center; gap:6px; background:var(--blue-lt); color:var(--navy); border:1px dashed var(--navy-lt); border-radius:9px; padding:8px 14px; font-size:12px; font-weight:600; cursor:pointer; }
.od-img-upload-btn.disabled { opacity:.5; cursor:not-allowed; }
.od-img-uploader-hint { font-size:11px; color:var(--text3); }
.od-img-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(150px, 1fr)); gap:14px; }
.od-img-card { position:relative; border:1px solid var(--border); border-radius:12px; overflow:hidden; background:var(--surface); }
.od-img-thumb { display:block; aspect-ratio:4/3; background:var(--bg); }
.od-img-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
.od-img-body { padding:8px 10px; }
.od-img-tit { font-size:12px; font-weight:700; color:var(--text); }
.od-img-sub { font-size:10.5px; color:var(--text3); margin-top:1px; }
.od-img-del { position:absolute; top:6px; right:6px; width:22px; height:22px; border-radius:50%; background:rgba(20,20,25,.55); color:#fff; border:none; font-size:11px; cursor:pointer; display:flex; align-items:center; justify-content:center; }
.od-img-del:hover { background:var(--red); }
`;
